import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'

const EASE = [0.16, 1, 0.3, 1]

/**
 * Last scene of the page: the invitation to start, next to what arrives at the door (the unboxing
 * laid out from above). The photo sits on the right on desktop (on top on phones); its edges melt into the page colour
 * so the text reads as part of the same picture.
 */
export default function ClosingCta({ eyebrow, title, subtitle, actions }) {
  const reduceMotion = useReducedMotion()
  const sectionRef = useRef(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })
  const drift = useTransform(scrollYProgress, [0, 1], ['-5%', '5%'])

  const rise = (delay) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.4 },
          transition: { duration: 1, ease: EASE, delay },
        }

  return (
    <section
      ref={sectionRef}
      className="relative isolate overflow-hidden border-t border-m1-line-soft lg:flex lg:min-h-[min(88svh,820px)] lg:items-center"
    >
      <div className="relative mx-auto aspect-[4/5] w-full max-w-[520px] overflow-hidden lg:absolute lg:inset-y-0 lg:right-0 lg:mx-0 lg:aspect-auto lg:w-[44%] lg:max-w-none">
        <motion.div
          className="absolute inset-0 scale-[1.12]"
          style={reduceMotion ? undefined : { y: drift }}
        >
          <motion.img
            src="/home/closing-kit.jpg"
            width="432"
            height="576"
            alt="Everything in the M1NT box laid out from above: the case with its graded card, the NFC tag, the certificate card, the sleeve and the protective trays"
            loading="lazy"
            decoding="async"
            draggable="false"
            className="block h-full w-full select-none object-cover"
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 0, scale: 1.08 },
                  whileInView: { opacity: 1, scale: 1 },
                  viewport: { once: true, amount: 0.2 },
                  transition: { duration: 1.8, ease: EASE },
                })}
          />
        </motion.div>
        <div className="m1nt-grain pointer-events-none absolute inset-0" aria-hidden="true" />
        {/* Melt every edge into the page colour. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, #0A090D 0%, rgba(10,9,13,0) 22%), linear-gradient(to top, #0A090D 0%, rgba(10,9,13,0) 34%)',
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            background:
              'linear-gradient(to right, #0A090D 0%, rgba(10,9,13,0) 16%, rgba(10,9,13,0) 84%, #0A090D 100%)',
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 hidden lg:block"
          style={{
            background:
              'linear-gradient(to right, #0A090D 0%, #0A090D 4%, rgba(10,9,13,0.8) 12%, rgba(10,9,13,0.4) 26%, rgba(10,9,13,0.12) 40%, rgba(10,9,13,0) 56%)',
          }}
          aria-hidden="true"
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-[var(--gutter)] pb-[var(--section)] pt-4 lg:py-[var(--section)]">
        <div className="max-w-[560px]">
          {eyebrow && (
            <motion.p className="text-[14px] text-m1-muted" {...rise(0)}>
              {eyebrow}
            </motion.p>
          )}
          <motion.h2
            className="mt-4 font-m1-display text-[clamp(36px,4.6vw,58px)] font-medium leading-[1.04] tracking-[-0.015em] text-m1-ink sm:font-normal"
            {...rise(0.12)}
          >
            {title}
          </motion.h2>
          <motion.p
            className="mt-6 max-w-[30em] text-[19px] font-light leading-relaxed text-m1-ink-soft"
            {...rise(0.24)}
          >
            {subtitle}
          </motion.p>
          <motion.div className="mt-8" {...rise(0.36)}>
            {actions}
          </motion.div>
        </div>
      </div>
    </section>
  )
}
